import { normalizeAreaName } from "@a2z/text";
import type {
  WhatsappBinding,
  WhatsappJson,
  WhatsappTemplate,
  WhatsappTemplateComponent,
} from "../types/whatsapp.types";
export function object(
  value: WhatsappJson | undefined,
): Record<string, WhatsappJson> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}
export function parseComponents(
  raw: string,
): WhatsappTemplateComponent[] | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !Array.isArray(parsed) ||
      !parsed.length ||
      parsed.some(
        (c) =>
          !c ||
          typeof c !== "object" ||
          Array.isArray(c) ||
          typeof c.type !== "string",
      )
    )
      return null;
    return parsed as WhatsappTemplateComponent[];
  } catch {
    return null;
  }
}
export function variables(text: string) {
  return [
    ...new Set(
      [...text.matchAll(/\{\{([^{}]+)\}\}/g)].map((match) => match[1]),
    ),
  ];
}
export function validVariables(text: string, format: string) {
  const params = variables(text);
  if (
    text.replace(/\{\{[^{}]+\}\}/g, "").includes("{{") ||
    text.replace(/\{\{[^{}]+\}\}/g, "").includes("}}")
  )
    return false;
  return format === "NAMED"
    ? params.every((p) => /^[a-z][a-z0-9_]*$/.test(p))
    : params.every((p, index) => p === String(index + 1));
}
export function exampleValues(
  component: WhatsappTemplateComponent,
  format: string,
): Record<string, string> {
  const params = variables(
    typeof component.text === "string" ? component.text : "",
  );
  const examples = object(component.example);
  const prefix = component.type === "HEADER" ? "header" : "body";
  if (format === "NAMED") {
    const entries = examples[`${prefix}_text_named_params`];
    return Object.fromEntries(
      Array.isArray(entries)
        ? entries.map((entry) => {
            const e = object(entry);
            return [String(e.param_name ?? ""), String(e.example ?? "")];
          })
        : [],
    );
  }
  const text = examples[`${prefix}_text`];
  const vals =
    component.type === "BODY" && Array.isArray(text) ? text[0] : text;
  return Object.fromEntries(
    params.map((p, i) => [p, Array.isArray(vals) ? String(vals[i] ?? "") : ""]),
  );
}
export function withExamples(
  component: WhatsappTemplateComponent,
  format: string,
  values: Record<string, string>,
): WhatsappTemplateComponent {
  const params = variables(
    typeof component.text === "string" ? component.text : "",
  );
  const prefix = component.type === "HEADER" ? "header" : "body";
  const next = { ...component };
  if (!params.length) {
    delete next.example;
    return next;
  }
  next.example =
    format === "NAMED"
      ? {
          [`${prefix}_text_named_params`]: params.map((param_name) => ({
            param_name,
            example: values[param_name] ?? "",
          })),
        }
      : {
          [`${prefix}_text`]:
            component.type === "BODY"
              ? [params.map((p) => values[p] ?? "")]
              : params.map((p) => values[p] ?? ""),
        };
  return next;
}
function hasOnly(value: Record<string, WhatsappJson>, keys: string[]) {
  return Object.keys(value).every((k) => keys.includes(k));
}
export function canVisualEdit(
  components: WhatsappTemplateComponent[],
  category: string,
) {
  if (category === "AUTHENTICATION") return false;
  const types = components.map((c) => c.type);
  if (new Set(types).size !== types.length) return false;
  return components.every((c) => {
    if (c.type === "HEADER")
      return (
        c.format === "TEXT" &&
        hasOnly(c, ["type", "format", "text", "example"]) &&
        hasOnly(object(c.example), ["header_text", "header_text_named_params"])
      );
    if (c.type === "BODY")
      return (
        typeof c.text === "string" &&
        hasOnly(c, ["type", "text", "example"]) &&
        hasOnly(object(c.example), ["body_text", "body_text_named_params"])
      );
    if (c.type === "FOOTER")
      return typeof c.text === "string" && hasOnly(c, ["type", "text"]);
    if (c.type === "BUTTONS")
      return (
        hasOnly(c, ["type", "buttons"]) &&
        Array.isArray(c.buttons) &&
        c.buttons.every((b) => {
          const button = object(b);
          return (
            ["QUICK_REPLY", "URL", "PHONE_NUMBER"].includes(
              String(button.type),
            ) &&
            hasOnly(button, ["type", "text", "url", "phone_number", "example"])
          );
        })
      );
    return false;
  });
}
export function templateInUse(
  template: WhatsappTemplate,
  bindings: WhatsappBinding[],
) {
  return bindings.some(
    (b) =>
      b.enabled &&
      b.templateName === template.name &&
      b.languageCode === template.language,
  );
}
export function compatibleTemplate(
  template: WhatsappTemplate,
  binding: WhatsappBinding,
) {
  if (template.status !== "APPROVED" || template.parameter_format !== "NAMED")
    return false;
  if (
    binding.purpose === "abandoned_recovery" &&
    template.category !== "MARKETING"
  )
    return false;
  const bodies = template.components.filter((c) => c.type === "BODY");
  const names = [
    ...new Set(
      variables(typeof bodies[0]?.text === "string" ? bodies[0].text : "").map(
        (name) => name.trim(),
      ),
    ),
  ];
  if (
    bodies.length !== 1 ||
    names.length !== binding.requiredBodyParameters.length ||
    !binding.requiredBodyParameters.every((p) => names.includes(p))
  )
    return false;
  if (
    template.components.some(
      (c) =>
        !["BODY", "HEADER", "FOOTER", "BUTTONS"].includes(c.type) ||
        (c.type === "HEADER" &&
          (c.format !== "TEXT" ||
            variables(String(c.text ?? "")).length > 0)) ||
        (c.type === "FOOTER" && variables(String(c.text ?? "")).length > 0),
    )
  )
    return false;
  const groups = template.components.filter((c) => c.type === "BUTTONS");
  if (groups.length > 1) return false;
  const entries = Array.isArray(groups[0]?.buttons)
    ? groups[0].buttons.map(object)
    : [];
  if (
    entries.length !==
    binding.requiredQuickReplyButtons + (binding.requiredUrlButton ? 1 : 0)
  )
    return false;
  for (let index = 0; index < binding.requiredQuickReplyButtons; index += 1) {
    if (entries[index]?.type !== "QUICK_REPLY") return false;
    const labels = binding.requiredQuickReplyLabels?.find(
      (button) => button.index === index,
    )?.labels;
    if (
      labels &&
      !labels.some(
        (label) =>
          normalizeAreaName(label).toLowerCase() ===
          normalizeAreaName(String(entries[index].text ?? "")).toLowerCase(),
      )
    )
      return false;
  }
  if (binding.requiredUrlButton) {
    const expected = binding.requiredUrlButton;
    const button = entries[expected.index];
    if (
      button?.type !== "URL" ||
      ![expected.url, expected.url.replace("/ar/", "/en/")].includes(
        String(button.url),
      )
    )
      return false;
  }
  return true;
}
